import React from 'react';
import {
  Paper,
  Stack,
  Divider,
  Avatar,
  Typography,
  Box,
  Skeleton,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PeopleAltRounded,
  EmojiEventsRounded,
  CalendarMonthRounded,
  WarningAmberRounded,
} from '@mui/icons-material';

interface StatItem {
  label: string;
  value: number | undefined;
  icon: React.ElementType;
  color: string;
  avatarBg: string;
}

interface ScoreboardStripProps {
  players: number | undefined;
  leagues: number | undefined;
  matches: number | undefined;
  conflicts: number | undefined;
}

const STATS_CONFIG: Omit<StatItem, 'value'>[] = [
  { label: 'Players', icon: PeopleAltRounded, color: '#42A5F5', avatarBg: 'rgba(0,0,0,0.04)' },
  { label: 'Leagues', icon: EmojiEventsRounded, color: '#7E57C2', avatarBg: 'rgba(0,0,0,0.04)' },
  { label: 'Matches', icon: CalendarMonthRounded, color: '#0288D1', avatarBg: 'rgba(0,0,0,0.04)' },
  { label: 'Conflicts', icon: WarningAmberRounded, color: '#F4511E', avatarBg: 'rgba(0,0,0,0.04)' },
];

function StatCell({ stat, sx }: { stat: StatItem; sx?: object }) {
  const Icon = stat.icon;
  const loading = stat.value === undefined;

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: { xs: 2.5, sm: 3 },
        px: 2,
        cursor: 'default',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
        ...sx,
      }}
    >
      <Avatar
        sx={{
          bgcolor: stat.avatarBg,
          color: stat.color,
          width: 48,
          height: 48,
          mb: 1.5,
        }}
      >
        <Icon />
      </Avatar>
      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.75rem', sm: '2rem' },
          lineHeight: 1,
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '2rem',
        }}
      >
        {!loading ? (
          <Fade in><span>{stat.value}</span></Fade>
        ) : (
          <Skeleton width={40} height={32} />
        )}
      </Typography>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: '0.08em', lineHeight: 1.5 }}
      >
        {stat.label}
      </Typography>
    </Box>
  );
}

const ScoreboardStrip: React.FC<ScoreboardStripProps> = ({
  players,
  leagues,
  matches,
  conflicts,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const stats: StatItem[] = STATS_CONFIG.map((cfg) => {
    const valueMap: Record<string, number | undefined> = {
      Players: players,
      Leagues: leagues,
      Matches: matches,
      Conflicts: conflicts,
    };
    return { ...cfg, value: valueMap[cfg.label] };
  });

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: theme.shape.borderRadius * 2 + 'px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
      }}
    >
      {/* Gradient top bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: '#43A047',
        }}
      />

      {isSmall ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
          }}
        >
          {stats.map((stat, i) => (
            <StatCell
              key={stat.label}
              stat={stat}
              sx={{
                borderRight: i % 2 === 0 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none',
              }}
            />
          ))}
        </Box>
      ) : (
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
        >
          {stats.map((stat) => (
            <StatCell key={stat.label} stat={stat} />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default ScoreboardStrip;
